import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Star, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SectionLabel } from "@/components/bits";

function Stars({ value, onPick, size = 26 }) {
  return (
    <div className="flex gap-2" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onPick && onPick(n)} disabled={!onPick}
          data-testid={`review-star-${n}`}
          className={onPick ? "hover:scale-110 transition-transform" : "cursor-default"}>
          <Star size={size} className={n <= value ? "fill-white text-white" : "text-white/25"} />
        </button>
      ))}
    </div>
  );
}

export default function Review() {
  const { sessionId } = useParams();
  const [order, setOrder] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [item, setItem] = useState(null);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/orders/by-session/${sessionId}`)
      .then((r) => setOrder(r.data))
      .catch(() => setLoadError("We could not find that order — check your review link and try again."));
  }, [sessionId]);

  const delivered = order
    ? (order.items && order.items.length
        ? order.items
        : [{ slug: order.slug, product_name: order.product_name, tier: order.tier, keys: order.keys || [] }]
      ).filter((it) => (it.keys || []).length > 0)
    : [];

  useEffect(() => {
    if (delivered.length && !item) setItem(delivered[0]);
  }, [order]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!item) return;
    if (text.trim().length < 4) { toast.error("Tell us a little more — at least a few words"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { session_id: sessionId, slug: item.slug, rating, text: text.trim() });
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not publish your review");
    }
    setBusy(false);
  };

  if (loadError) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5 pt-24" data-testid="review-page">
        <div className="max-w-md w-full rounded-2xl border border-white/15 px-8 py-10 text-center">
          <XCircle size={28} className="mx-auto text-red-300" />
          <p className="mt-5 text-sm text-white/70 leading-relaxed" data-testid="review-error">{loadError}</p>
          <Link to="/store" className="btn-ghost inline-block mt-8 px-6 py-3 text-xs">BACK TO STORE</Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return <div className="pt-40 text-center font-mono text-xs tracking-[0.3em] text-white/40" data-testid="review-page">// LOADING YOUR ORDER...</div>;
  }

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5 pt-24" data-testid="review-page">
        <div className="max-w-md w-full border border-white/15 px-8 py-10 text-center" data-testid="review-success">
          <CheckCircle2 size={28} className="mx-auto text-emerald-200" />
          <h1 className="mt-5 font-display text-3xl uppercase">Review published</h1>
          <p className="mt-4 text-sm text-white/60 leading-relaxed">
            Thank you — your review is live on the store right now. It helps other buyers shop with confidence.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to={`/product/${item.slug}`} className="btn-inj px-6 py-3 text-xs" data-testid="review-see-live">SEE IT LIVE</Link>
            <Link to="/store" className="btn-ghost px-6 py-3 text-xs">BACK TO STORE</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-5 pt-32 pb-28" data-testid="review-page">
      <SectionLabel>RATE YOUR ORDER</SectionLabel>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl uppercase leading-[0.95]">How is it?</h1>
      <p className="mt-4 text-sm text-white/55 leading-relaxed">
        Your key{delivered.length > 1 ? "s are" : " is"} delivered — tell other buyers how {item ? item.product_name : "it"} treats you.
        Reviews post publicly and instantly.
      </p>

      {delivered.length === 0 ? (
        <div className="mt-10 border border-amber-300/30 px-6 py-6 font-mono text-[11px] tracking-[0.12em] text-amber-100/80" data-testid="review-pending-note">
          // YOUR KEY IS STILL BEING PREPARED — YOU CAN REVIEW AS SOON AS IT IS DELIVERED
        </div>
      ) : (
        <div className="mt-10 rounded-2xl border border-white/15 px-6 py-7">
          {delivered.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-7">
              {delivered.map((it) => (
                <button key={it.slug} onClick={() => setItem(it)} data-testid={`review-item-${it.slug}`}
                  className={`px-4 py-2 rounded-full font-mono text-[10px] tracking-[0.15em] border transition-colors ${
                    item?.slug === it.slug ? "border-white bg-white text-black" : "border-white/20 text-white/60 hover:border-white/50"
                  }`}>
                  {it.product_name}
                </button>
              ))}
            </div>
          )}

          <div className="font-mono text-[10px] tracking-[0.2em] text-white/40 mb-3">// YOUR RATING</div>
          <Stars value={rating} onPick={setRating} />

          <div className="mt-7 font-mono text-[10px] tracking-[0.2em] text-white/40 mb-3">// YOUR REVIEW</div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 600))}
            rows={5}
            placeholder="Undetected? Smooth setup? Support experience? Be honest — this posts publicly."
            data-testid="review-text"
            className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white/85 leading-relaxed focus:border-white focus:outline-none placeholder:text-white/25 resize-none"
          />
          <div className="mt-1 text-right font-mono text-[9px] text-white/25">{text.length}/600</div>

          <div className="mt-5 font-mono text-[10px] tracking-[0.15em] text-white/40">
            POSTING AS <span className="text-white/80">{order.discord?.global_name || order.discord?.username || "VERIFIED BUYER"}</span>
          </div>

          <button onClick={submit} disabled={busy || !item} data-testid="review-submit"
            className="btn-inj w-full mt-6 py-4 text-xs flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? <><Loader2 size={14} className="animate-spin" /> PUBLISHING...</> : "PUBLISH REVIEW"}
          </button>
        </div>
      )}
    </div>
  );
}
